/**
 * ExpectedArrivalsRail — staff Today page rail (migration 0079).
 *
 * Lists arrivals (drop-off + pick-up) whose ETA falls in the next 60 minutes.
 * Each row shows a live countdown, vehicle plate, customer name, and a
 * "Mark ready" button that flips status → ready_for_customer (fires push +
 * sms to the customer per their notification preferences).
 *
 * Subscribes to ARRIVAL_STATUS_CHANGED realtime events so the rail refreshes
 * without polling beyond the initial fetch.
 */
import { Car, Check } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ArrivalRow {
  id: string;
  bookingId: string;
  facilityId: string | null;
  petId: string | null;
  kind: 'dropoff' | 'pickup';
  status:
    | 'expected'
    | 'en_route'
    | 'ready_for_customer'
    | 'in_progress'
    | 'completed'
    | 'no_show'
    | 'late';
  etaAt: string | null;
  vehiclePlate: string | null;
  customerArrivedAt: string | null;
  staffMarkedReadyAt: string | null;
  customerFirstName: string;
  customerLastName: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  /** Optional facility filter; when set, only rows for this facility render. */
  facilityId?: string | null;
}

export function ExpectedArrivalsRail({ facilityId }: Props) {
  const [rows, setRows] = useState<ArrivalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/arrivals/today?windowMinutes=60', { credentials: 'include' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = (await res.json()) as { arrivals: ArrivalRow[] };
      setRows(data.arrivals);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-tick the countdown every 15 seconds. Doesn't refetch — purely local.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(
    () => (facilityId ? rows.filter((r) => !r.facilityId || r.facilityId === facilityId) : rows),
    [rows, facilityId],
  );

  const handleReady = useCallback(
    async (row: ArrivalRow) => {
      try {
        const res = await fetch(`/api/bookings/${row.bookingId}/arrivals/${row.id}/ready`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) return;
        void load();
      } catch {
        /* noop */
      }
    },
    [load],
  );

  if (loading) {
    return (
      <section data-testid="expected-arrivals-rail">
        <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Expected arrivals
        </h2>
        <div className="rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-xs text-muted-foreground">
          Loading…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section data-testid="expected-arrivals-rail">
        <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Expected arrivals
        </h2>
        <div className="rounded-xl border border-dashed border-destructive/40 bg-destructive/10 px-4 py-3 text-xs text-destructive">
          Could not load arrivals.
        </div>
      </section>
    );
  }

  return (
    <section data-testid="expected-arrivals-rail">
      <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        Expected arrivals
      </h2>
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-xs text-muted-foreground">
            No one heading in within the next hour.
          </div>
        ) : (
          filtered.map((r) => {
            const etaMs = r.etaAt ? new Date(r.etaAt).getTime() : 0;
            const diffMin = etaMs > 0 ? Math.round((etaMs - now) / 60_000) : 0;
            const countdown = diffMin <= 0 ? 'Now' : diffMin === 1 ? '1 min' : `${diffMin} min`;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-border bg-surface px-4 py-3"
                data-testid={`arrival-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {r.customerFirstName} {r.customerLastName}{' '}
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        {r.kind === 'dropoff' ? 'drop off' : 'pick up'}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      ETA <span className="num font-medium">{countdown}</span>
                      {r.vehiclePlate ? (
                        <span className="ml-2 inline-flex items-center gap-1">
                          <Car className="h-3 w-3" aria-hidden /> {r.vehiclePlate}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  {r.status === 'ready_for_customer' ? (
                    <span
                      className="rounded-md bg-emerald-100 px-2 py-1 text-[10.5px] font-medium uppercase text-emerald-700"
                      data-testid={`arrival-${r.id}-ready`}
                    >
                      Ready
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleReady(r)}
                      className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                      data-testid={`arrival-${r.id}-mark-ready`}
                    >
                      <Check className="h-3 w-3" aria-hidden /> Mark ready
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
